/* eslint-disable react-hooks/exhaustive-deps */
import {
  InputChangeEvent,
  InputProps as KendInputProps,
  Input as KendoInput,
} from '@progress/kendo-react-inputs';
import _ from 'lodash';
import React, { useState } from 'react';
import { ValidationError } from '../../apps/benefit/models/Errors';
import useToggleClass from '../hooks/useToggleClass';
import ErrorViewer from './ErrorViewer';
import Icons from './Icons';

export interface InputProps {
  controlClassName?: string;
  mandatory?: boolean;
  helper?: string;
  onChange?: (e: InputChangeEvent) => void;
  errors?: ValidationError[];
  clearErrors?: () => void;
  autocomplete?: string;
  // custom onBlur logic, to avoid the blur handler being called when the visibility is toggled
  onBlur?: (e: { inputTarget: HTMLInputElement }) => void;
}

interface IVisivilityState {
  isPasswordVisible: boolean;
}

export default function Password(
  props: InputProps & Omit<KendInputProps, 'onBlur'>,
) {
  const componentRef = React.useRef<HTMLDivElement | null>(null);
  const passwordContainerRef = React.useRef<HTMLDivElement | null>(null);

  const { addClass, removeClass } = useToggleClass();
  const {
    controlClassName,
    mandatory,
    helper,
    id,
    label,
    errors,
    onChange,
    clearErrors,
    onBlur,
  } = props;

  const internalOnChange = (e: InputChangeEvent) => {
    if (errors?.length) {
      clearErrors?.();
    }
    onChange?.(e);
  };

  const classNames = `control-group ${controlClassName ?? ''}`.trim();

  const [visibilityState, setVisibilityState] = useState<IVisivilityState>({
    isPasswordVisible: false,
  });

  const toggleOldPassword = () => {
    setVisibilityState(prev => ({
      ...prev,
      isPasswordVisible: !prev.isPasswordVisible,
    }));
  };

  React.useEffect(() => {
    errors && errors?.length > 0
      ? addClass('control-error', componentRef)
      : removeClass('control-error', componentRef);
  }, [errors]);

  return (
    <div className={classNames} ref={componentRef}>
      <div className="control-content">
        {!!label && (
          <label htmlFor={id}>
            {label}
            {mandatory && <sup>*</sup>}
          </label>
        )}
        <div
          className="password-container"
          ref={passwordContainerRef}
          onBlur={e => {
            if (e.currentTarget.contains(e.relatedTarget)) {
              // focus is still on the div, do nothing
              return;
            }
            const input = passwordContainerRef.current?.querySelector('input');
            if (input) {
              onBlur?.({ inputTarget: input });
            }
          }}
        >
          <KendoInput
            {..._.omit(props, ['label', 'mandatory', 'clearErrors', 'onBlur'])}
            id={id}
            onChange={internalOnChange}
            autoComplete={props.autocomplete ?? 'off'}
            type={visibilityState.isPasswordVisible ? 'text' : 'password'}
          />
          <button
            className="icon-password-button"
            type="button"
            onClick={toggleOldPassword}
          >
            {visibilityState.isPasswordVisible ? (
              <Icons.PasswordHide />
            ) : (
              <Icons.PasswordShow />
            )}
          </button>
        </div>
      </div>
      {!!helper && <div className="control-info">{helper}</div>}
      <ErrorViewer errors={errors} />
    </div>
  );
}
